import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Collection } from 'models/Collection';
import { collectionsQuery } from 'networking/mindfeed/queries/collections';
import { deleteCollection } from 'networking/mindfeed/services/collection.service';
import { isContext } from 'utils/ContextUtils';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';

type Context = { previousCollections: Collection[] };

const useDeleteCollection = ({
  onSuccess,
  removeOnSuccess = false,
}: {
  onSuccess?: () => void;
  removeOnSuccess?: boolean;
}): ReturnType<typeof useMutation> => {
  const queryClient = useQueryClient();
  const { selectedBox } = useActiveSpace();

  const deleteCollectionMutation = useMutation({
    mutationFn: deleteCollection,
    onMutate: async (id) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: collectionsQuery(selectedBox?.id).queryKey,
      });

      // Snapshot the previous value
      const previousCollections = queryClient.getQueryData<Collection[]>(
        collectionsQuery(selectedBox?.id).queryKey,
      );

      // Optimistically update to the new value
      if (previousCollections) {
        queryClient.setQueryData<Collection[]>(
          collectionsQuery(selectedBox?.id).queryKey,
          previousCollections.filter((collection) => {
            return collection.id !== id;
          }),
        );
      }

      return { previousCollections };
    },
    onSuccess: async () => {
      if (removeOnSuccess) {
        queryClient.removeQueries({
          queryKey: collectionsQuery(selectedBox?.id).queryKey,
        });
      }
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (isContext<Context>(context, 'previousCollections')) {
        queryClient.setQueryData<Collection[]>(
          collectionsQuery(selectedBox?.id).queryKey,
          context.previousCollections,
        );
      }
    },
  });

  return deleteCollectionMutation;
};

export default useDeleteCollection;
